import React, { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { toast } from "sonner";

// Known migration-state sentinel keys surfaced to admins. Each entry is a
// read-only report; the backend endpoint /api/migration-state/{key} is admin
// gated.
const REPORTS = [
  {
    key: "prod_cleanup_test_items_v1",
    title: "Test-item cleanup",
    description:
      "One-time cleanup of two pre-existing TEST linen artifacts (TEST_FIX3_ed8c94, TEST_deactivation_probe) and their historical ledger/audit refs.",
  },
];

function CountRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm py-1">
      <span className="text-slate-600">{label}</span>
      <span className="font-semibold text-slate-900 tabular-nums">{value}</span>
    </div>
  );
}

function pickReport(doc) {
  // The cleanup script writes the counts under different shapes across
  // dryrun/execute. Normalise to a flat object the admin can read.
  if (!doc) return null;
  const r =
    doc.report ||
    doc.execute_report ||
    doc.dryrun_report ||
    doc.result ||
    doc;
  return r;
}

function ReportCard({ entry }) {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.get(
        `/migration-state/${encodeURIComponent(entry.key)}`
      );
      setDoc(data);
    } catch (e) {
      if (e?.response?.status === 404) {
        setErr("No report found for this key yet.");
      } else {
        setErr(formatApiErrorDetail(e) || "Failed to load");
        toast.error("Failed to load migration report");
      }
    } finally {
      setBusy(false);
    }
  };

  const report = pickReport(doc);
  return (
    <Card className="p-4 mb-3" data-testid={`migration-report-${entry.key}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-semibold text-slate-900">{entry.title}</div>
          <div className="text-xs text-slate-500 mt-0.5">{entry.description}</div>
          <div className="text-[11px] text-slate-400 mt-1 font-mono">
            key: {entry.key}
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={load}
          disabled={busy}
          data-testid={`load-report-${entry.key}`}
        >
          {busy ? "Loading..." : doc ? "Refresh" : "Load report"}
        </Button>
      </div>

      {err && (
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2">
          {err}
        </div>
      )}

      {doc && (
        <div className="mt-3 border-t border-slate-200 pt-3 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-slate-500">Status: </span>
              <span className="font-semibold">
                {doc.status || doc.state || "—"}
              </span>
            </div>
            <div>
              <span className="text-slate-500">Mode: </span>
              <span className="font-semibold">{doc.mode || "—"}</span>
            </div>
            <div>
              <span className="text-slate-500">Executed at: </span>
              <span className="font-mono text-xs">
                {doc.executed_at || doc.updated_at || doc.timestamp || "—"}
              </span>
            </div>
            <div>
              <span className="text-slate-500">Report id: </span>
              <span className="font-mono text-xs">
                {doc.report_id || "—"}
              </span>
            </div>
          </div>

          {report && typeof report === "object" && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
                Counts
              </div>
              <div className="divide-y divide-slate-100">
                {Object.entries(report)
                  .filter(([k, v]) => typeof v === "number")
                  .map(([k, v]) => (
                    <CountRow key={k} label={k} value={v} />
                  ))}
                {Object.entries(report)
                  .filter(([k, v]) => Array.isArray(v))
                  .map(([k, v]) => (
                    <CountRow key={k} label={`${k}.length`} value={v.length} />
                  ))}
              </div>
            </div>
          )}

          <details className="text-xs">
            <summary className="cursor-pointer text-slate-500">
              Raw document
            </summary>
            <pre className="mt-2 bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto text-[11px]">
              {JSON.stringify(doc, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </Card>
  );
}

export function MigrationReportsPanel() {
  return (
    <div data-testid="migration-reports-panel">
      <div className="text-sm text-slate-600 mb-3">
        Read-only view of one-time migration/cleanup sentinel documents. Admin
        only.
      </div>
      {REPORTS.map((r) => (
        <ReportCard key={r.key} entry={r} />
      ))}
    </div>
  );
}
